import React, { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';
import { type AuditEvent, type ProgramDto } from 'src/store/generated/leela-api-generated';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import DatePicker from '@amzn/meridian/date-picker';
import TimePicker from '@amzn/meridian/time-picker';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { Loader } from 'src/components/common/Loader';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { useTranslation } from 'src/arb-wrapper';
import { NoResultsTableRow } from 'src/components/common/data-table-action-bar/NoResultsTableRow';
import { useEventsControllerFindManyQuery } from 'src/store/leela-api';

interface ErrorType { status: number; data: { statusCode: number; message: string } };

enum DEFAULT_TIMES_AGO {
  NOW,
  HOUR,
  DAY,
  WEEK,
  MONTH,
};

const getTimeAgo = (defaultTimeAgo: DEFAULT_TIMES_AGO) => {
  const now = new Date();

  switch (defaultTimeAgo) {
    case DEFAULT_TIMES_AGO.NOW:
      break;
    case DEFAULT_TIMES_AGO.HOUR:
      now.setUTCHours(now.getUTCHours() - 1);
      break;
    case DEFAULT_TIMES_AGO.DAY:
      now.setUTCDate(now.getUTCDate() - 1);
      break;
    case DEFAULT_TIMES_AGO.WEEK:
      now.setUTCDate(now.getUTCDate() - 7);
      break;
    case DEFAULT_TIMES_AGO.MONTH:
      now.setUTCMonth(now.getUTCMonth() - 1);
      break;
  }

  return now;
};

const timeLocale = 'en-US';
const timeOptions = { timeZone: 'UTC', hour12: false };

export const AuditContent = (props: { program: ProgramDto }) => {
  const { program } = props;
  const { t } = useTranslation();

  const oneDayAgo = new Date();
  oneDayAgo.setUTCDate(oneDayAgo.getUTCDate() - 1);
  const [startDateTime, setStartDateTime] = useState<Date>(oneDayAgo);
  const [endDateTime, setEndDateTime] = useState<Date>(new Date());
  const [filters, setFilters] = useState<{ startTime: string; endTime: string }>({ startTime: oneDayAgo.toISOString(), endTime: new Date().toISOString() });

  const [isWaiting, setIsWaiting] = useState(false);

  const {
    data,
    error,
    isLoading,
  } = useEventsControllerFindManyQuery({
    programId: program.programId,
    ...filters,
  });

  const errorStatus = (error as ErrorType)?.status;
  const errorMessage = (error as ErrorType)?.data?.message;

  const setDateAndTime = (dateTime: Date, setDateTime: Dispatch<SetStateAction<Date>>, date?: string, time?: string) => {
    const newDateTime = new Date(dateTime);
    if (date) {
      const newDate = new Date(date);
      newDateTime.setUTCFullYear(newDate.getUTCFullYear());
      newDateTime.setUTCMonth(newDate.getUTCMonth());
      newDateTime.setUTCDate(newDate.getUTCDate());
    }
    if (time) {
      const timeParts = time.split(':');
      newDateTime.setUTCHours(parseInt(timeParts[0]));
      newDateTime.setUTCMinutes(parseInt(timeParts[1]));
    }
    setDateTime(newDateTime);
  };

  const applyFilters = useCallback((defaultTimeAgo?: DEFAULT_TIMES_AGO) => {
    setIsWaiting(true);

    if (defaultTimeAgo) {
      const selectedDefaultStart = getTimeAgo(defaultTimeAgo);
      const now = getTimeAgo(DEFAULT_TIMES_AGO.NOW);
      setFilters({
        startTime: selectedDefaultStart.toISOString() ?? '',
        endTime: now.toISOString() ?? '',
      });
      setStartDateTime(selectedDefaultStart);
      setEndDateTime(now);
    } else {
      setFilters({
        startTime: startDateTime.toISOString() ?? '',
        endTime: endDateTime.toISOString() ?? '',
      });
    }
  }, [startDateTime, endDateTime]);

  const getDescription = (event: AuditEvent) => {
    const operation = event.operation;

    let childIds: Array<{ id: string; publisher?: string }> = [];
    if (event.collection === 'campaigns') {
      childIds = event.ids.slice(1);
    }

    switch (operation) {
      case 'PROGRAM_CREATED':
        return t('audit_program_created_message');
      case 'PROGRAM_UPDATED':
        return t('audit_program_updated_message');
      case 'PROGRAM_DELETED':
        return t('audit_program_deleted_message');
      case 'CAMPAIGN_DESIRED_CREATED':
        return t('audit_campaign_created_message', { campaigns: JSON.stringify(childIds.map(obj => obj.publisher ?? '')) });
      case 'CAMPAIGN_DESIRED_UPDATED':
        return t('audit_campaign_updated_message', { campaigns: JSON.stringify(childIds.map(obj => obj.publisher ?? '')) });
      case 'CAMPAIGN_DESIRED_DELETED':
        return t('audit_campaign_deleted_message', { campaigns: JSON.stringify(childIds.map(obj => obj.publisher ?? '')) });
      case 'ASSET_CREATED':
        return t('audit_asset_created_message');
      case 'ASSET_UPDATED':
        return t('audit_asset_updated_message');
      case 'ASSET_DELETED':
        return t('audit_asset_deleted_message');
    }
  };

  useEffect(() => {
    setIsWaiting(false);
  }, [data]);

  useEffect(() => {
    errorMessage && setIsWaiting(false);
  }, [errorMessage]);

  return (
    <Column>
      <Row alignmentHorizontal="start" widths={['fit']} wrap="down">
        <Button type="secondary" onClick={() => { applyFilters(DEFAULT_TIMES_AGO.HOUR); }}>Last hour</Button>
        <Button type="secondary" onClick={() => { applyFilters(DEFAULT_TIMES_AGO.DAY); }}>Last day</Button>
        <Button type="secondary" onClick={() => { applyFilters(DEFAULT_TIMES_AGO.WEEK); }}>Last week</Button>
        <Button type="secondary" onClick={() => { applyFilters(DEFAULT_TIMES_AGO.MONTH); }}>Last month</Button>
      </Row>

      <Row alignmentHorizontal="start" alignmentVertical="bottom" widths={['fit']}>
        <DatePicker data-testid="start-date-picker" label={t('start_date_utc')} size="small" value={startDateTime.toISOString()} onChange={(date: string) => { setDateAndTime(startDateTime, setStartDateTime, date, undefined); }} />
        <TimePicker data-testid="start-time-picker" size="small" value={startDateTime.toLocaleTimeString(timeLocale, timeOptions)} onChange={(time: string) => { setDateAndTime(startDateTime, setStartDateTime, undefined, time); }} />
      </Row>
      <Row alignmentHorizontal="start" alignmentVertical="bottom" widths={['fit']}>
        <DatePicker data-testid="end-date-picker" label={t('end_date_utc')} size="small" value={endDateTime.toISOString()} onChange={(date: string) => { setDateAndTime(endDateTime, setEndDateTime, date, undefined); }} />
        <TimePicker data-testid="end-time-picker" size="small" value={endDateTime.toLocaleTimeString(timeLocale, timeOptions)} onChange={(time: string) => { setDateAndTime(endDateTime, setEndDateTime, undefined, time); }} />
        <Button size="small" type="tertiary" onClick={() => { setEndDateTime(getTimeAgo(DEFAULT_TIMES_AGO.NOW)); }}>Now</Button>
      </Row>
      <Row alignmentHorizontal="start" widths={['fit']}>
        <Button onClick={applyFilters}>Apply</Button>
      </Row>

      <Loader isLoading={isLoading || isWaiting}>
        {errorMessage &&
          <>
            <Row alignmentHorizontal="center" widths={['fit']}>
              <Heading level={2}>{`${errorStatus}: ${errorMessage}`}</Heading>
            </Row>
            <Row alignmentHorizontal="center" widths={['fit']}>
              <Text>Please try a shorter time frame.</Text>
            </Row>
          </>
        }
        {!errorMessage &&
          <Table
            headerRows={1}
            rowComponents={[NoResultsTableRow, TableRow]}
            spacing="medium"
          >
            <TableRow>
              <TableCell width={250}>Date</TableCell>
              <TableCell width={100}>Type</TableCell>
              <TableCell width={100}>User</TableCell>
              <TableCell width={100}>Level</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
            {
              data?.map((event: AuditEvent, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell>{event.timestamp}</TableCell>
                    <TableCell>UI</TableCell>
                    <TableCell>{event.user}</TableCell>
                    <TableCell>{event.level}</TableCell>
                    <TableCell>{event.message ?? getDescription(event)}</TableCell>
                  </TableRow>
                );
              })
            }
            { (data?.length === 0 && !errorMessage) && <NoResultsTableRow span={5} />}
          </Table>
        }
      </Loader>
    </Column>
  );
};
